import { type VariantProps, cva } from "class-variance-authority";
import { type JSX, splitProps } from "solid-js";
import { cn } from "~/util/utils";

const textVariant = cva("inline-block", {
	variants: {
		size: {
			default: "text-md",
			xs: "text-xs",
			sm: "text-sm",
			md: "text-md",
			lg: "text-lg",
			xl: "text-xl",
		},
		variant: {
			accent: "text-accent",
			primary: "text-primary",
			muted: "text-gray-500",
		},
	},
	defaultVariants: {
		size: "default",
		variant: undefined,
	},
});

interface TextProps extends JSX.HTMLAttributes<HTMLParagraphElement>, VariantProps<typeof textVariant> {}

const textSplitProps = ["class", "variant", "size"] as const;

function Text(props: TextProps) {
	const [local, other] = splitProps(props, textSplitProps);
	return (
		<p
			class={cn(textVariant({ size: local.size, variant: local.variant }), local.class)}
			{...other}
		/>
	);
}

function BoldText(props: TextProps) {
	const [local, other] = splitProps(props, textSplitProps);
	return (
		<Text
			class={cn(textVariant({ size: local.size, variant: local.variant }), "font-bold", local.class)}
			{...other}
		/>
	);
}

function MediumText(props: TextProps) {
	const [local, other] = splitProps(props, textSplitProps);
	return (
		<Text
			class={cn(textVariant({ size: local.size, variant: local.variant }), "font-medium", local.class)}
			{...other}
		/>
	);
}

function SemiBoldText(props: TextProps) {
	const [local, other] = splitProps(props, textSplitProps);
	return (
		<Text
			class={cn(textVariant({ size: local.size, variant: local.variant }), "font-semibold", local.class)}
			{...other}
		/>
	);
}

export { Text, BoldText, MediumText, SemiBoldText, textVariant };
export type { TextProps };
